import React, { createContext, useContext, useMemo } from 'react';

import { useHttpClientIntegration } from '@/shared/infra/http/HttpClientContext';
import { ApiPendingEverifyRepository } from '../repositories/ApiPendingEverifyRepository';
import { FetchPendingEverifyItemsUseCase } from '../../application/use-cases/FetchPendingEverifyItemsUseCase';
import { IPendingEverifyRepository } from '../../application/ports/outbound/IPendingEverifyRepository';
import { FetchSingleEverifyItemUseCase } from '../../application/use-cases/FetchSingleEverifyItemUseCase';
import { RecordDelayReasonUseCase } from '../../application/use-cases/RecordDelayReasonUseCase';

interface PendingEverifyDependencies {
  pendingEverifyRepository: IPendingEverifyRepository;
  fetchPendingEverifyItemsUseCase: FetchPendingEverifyItemsUseCase;
  fetchSingleEverifyItemUseCase: FetchSingleEverifyItemUseCase;
  recordDelayReasonUseCase: RecordDelayReasonUseCase;
}

const PendingEverifyDIContext = createContext<
  PendingEverifyDependencies | undefined
>(undefined);

export const PendingEverifyDIProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const httpClient = useHttpClientIntegration();

  const dependencies = useMemo(() => {
    const pendingEverifyRepository = new ApiPendingEverifyRepository(httpClient);
    const fetchPendingEverifyItemsUseCase = new FetchPendingEverifyItemsUseCase(
      pendingEverifyRepository
    );
    const fetchSingleEverifyItemUseCase = new FetchSingleEverifyItemUseCase(
      pendingEverifyRepository
    );

    const recordDelayReasonUseCase = new RecordDelayReasonUseCase(pendingEverifyRepository);

    return {
      pendingEverifyRepository,
      fetchPendingEverifyItemsUseCase,
      fetchSingleEverifyItemUseCase,
      recordDelayReasonUseCase,
    };
  }, [httpClient]);

  return (
    <PendingEverifyDIContext.Provider value={dependencies}>
      {children}
    </PendingEverifyDIContext.Provider>
  );
};

export const usePendingEverifyDI = (): PendingEverifyDependencies => {
  const context = useContext(PendingEverifyDIContext);
  if (!context) {
    throw new Error(
      'usePendingEverifyDI must be used within a PendingEverifyDIProvider'
    );
  }
  return context;
};
